import { Box } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Header, Page } from 'redesign';
import { routes } from 'Routes';
import { invariant } from 'shared/utils/utils';
import { useAlert } from 'shared/components/alerts';
import { RenderUnsettledUI } from 'shared/components';
import { useSiteDetection } from 'shared/hooks';
import { useGetFlexPoolsForFlexWorker } from 'hq/components/views/flexWorkers/flex-worker-detail/tabs/flex-pools/use-get-flex-pools-for-flex-worker';
import { useTranslation } from 'react-i18next';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { FlexPool } from 'hq/services/flex-pool-service.types';
import { EmployeeForm, EmployeeFormData } from '../employee-form/employee-form';
import { useUpdateEmployee } from '../../hooks/use-update-employee';
import { useGetEmployeeDetail } from '../../hooks/use-get-employee-detail';

type EmployeeUpdateProps = {
  id: string;
  employee: {
    email: string;
    firstName: string;
    infix?: string | null;
    lastName: string;
  };
  flexPools: FlexPool[];
};

export const EmployeeUpdate = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Flex worker ID must be passed as a route param');

  const { site } = useSiteDetection();
  const employeeQuery = useGetEmployeeDetail(id ?? '');
  const flexPoolsQuery = useGetFlexPoolsForFlexWorker(site, id);

  if (employeeQuery.status !== 'success' || flexPoolsQuery.status !== 'success') {
    return <RenderUnsettledUI data={flexPoolsQuery} />;
  }

  return (
    <EmployeeUpdateImpl id={id} employee={employeeQuery.data} flexPools={flexPoolsQuery.data} />
  );
};

export const EmployeeUpdateImpl = ({ id, employee, flexPools }: EmployeeUpdateProps) => {
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);

  const formMethods = useForm<EmployeeFormData>({
    defaultValues: {
      email: employee.email,
      firstName: employee.firstName,
      infix: employee.infix ?? '',
      lastName: employee.lastName,
      employeeFlexPools: flexPools,
    },
  });

  const employeeMutation = useUpdateEmployee(id);

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      employeeMutation.mutate(data, {
        onSuccess: () => {
          navigate(generatePath(routes.EmployeeDetail, { id }));
          alertSuccess(
            t('employee_edit_screen.update_employee_success', { ns: 'ogp-screen-specific' })
          );
        },
        onError: (err) => alertError(err),
      });
    })();
  };

  return (
    <Page
      header={<Header titleText={t('employee_edit_screen.title', { ns: 'ogp-screen-specific' })} />}
      toolbox={
        <ToolboxForm
          onSubmit={handleSubmit}
          mutationStatus={employeeMutation.status}
          mode="update"
          onCancel={() => navigate(generatePath(routes.Workforce))}
        />
      }
    >
      <Box width="100%">
        <FormProvider {...formMethods}>
          <EmployeeForm formState="update" />
        </FormProvider>
      </Box>
    </Page>
  );
};
